import {
    createScope,
    getIndustryById,
    IApiAxiosError,
    IIndustry,
    IScopeCreate,
} from "@/api";
import {
    DocumentPickerValue,
    DocumentSourceSection,
} from "@/components/shared/document-picker";
import { IndustryAutocomplete } from "@/components/shared/IndustryAutocomplete";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { FileError } from "@/config/constants";
import { useAuth } from "@/contexts/auth/useAuth";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { trackDocumentUploaded, trackScopeCreated } from "@/lib/analytics";
import {
    alpha,
    Box,
    Checkbox,
    FormControlLabel,
    Stack,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { ClauseChecklistAutocomplete } from "../../form-elements/ClauseChecklistAutocomplete";

// Styled components for premium feel
const FormSection = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const FormCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2.5),
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
        borderColor: theme.palette.grey[300],
    },
    "&:focus-within": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
    color: theme.palette.grey[400],
    "&.Mui-checked": {
        color: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
        fontSize: 20,
    },
}));

interface IFormData {
    organization_id: number;
    document_title: string;
    clause_checklist_id?: number;
    document_object_name: string | undefined;
    library_document_id?: number;
    field_extraction_enabled?: boolean;
    industry_id?: number;
}

interface IProps {
    organizationId: number;
    organizationIndustryId?: number | null;
    initialDocument?: DocumentPickerValue | null;
    /**
     * Client preset (Unit 3.4): the created scope is tagged with this
     * client at birth. Omitted for the global flow.
     */
    clientId?: number;
}

export const CreateScopeDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const {
        organizationId,
        organizationIndustryId,
        initialDocument,
        clientId,
    } = payload;
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { organization, settings } = useOrganization();
    const { user } = useAuth();
    const queryClient = useQueryClient();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [documentValue, setDocumentValue] =
        useState<DocumentPickerValue | null>(initialDocument ?? null);

    const methods = useForm<IFormData>({
        defaultValues: {
            organization_id: organization.id,
            document_title: initialDocument
                ? initialDocument.title.replace(/\.pdf$/i, "")
                : "",
            clause_checklist_id: undefined,
            document_object_name: initialDocument?.objectName ?? undefined,
            library_document_id:
                initialDocument?.source === "library"
                    ? initialDocument.libraryDocumentId
                    : undefined,
            field_extraction_enabled: settings.field_extraction_enabled,
            industry_id: organizationIndustryId ?? undefined,
        },
    });

    const {
        control,
        watch,
        formState: { errors },
        getValues,
        setValue,
        setError,
        clearErrors,
        handleSubmit: rhfHandleSubmit,
    } = methods;

    const industryId = watch("industry_id");
    const documentObjectName = watch("document_object_name");

    const { data: industryData } = useQuery({
        queryKey: ["industry", industryId],
        queryFn: () => getIndustryById(industryId!),
        enabled: !!industryId,
    });

    const { mutate, error } = useMutation({
        mutationFn: async (data: IFormData) => {
            if (!data.clause_checklist_id || !data.document_object_name) {
                throw new Error("Missing required fields");
            }

            const scopeCreate: IScopeCreate = {
                organization_id: data.organization_id,
                document_title: data.document_title || "Untitled Document",
                clause_checklist_id: data.clause_checklist_id,
                field_extraction_enabled: data.field_extraction_enabled,
                industry_id: data.industry_id,
                ...(clientId !== undefined && { client_id: clientId }),
                ...(data.library_document_id
                    ? { document_id: data.library_document_id }
                    : { document_file: data.document_object_name }),
            };

            return createScope(scopeCreate);
        },
        onMutate: () => {
            setIsSubmitting(true);
        },
        onSuccess: (createdScope, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-organization-scopes"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-organization-recent-scopes"],
            });

            const isLibraryPick = variables.library_document_id !== undefined;

            // Pydantic `int | None` arrives as JSON null, so guard with typeof rather than `!== undefined`.
            if (typeof createdScope.scope_id === "number") {
                trackScopeCreated({
                    scope_id: createdScope.scope_id,
                    clause_checklist_id: variables.clause_checklist_id!,
                    industry_id: variables.industry_id,
                    field_extraction_enabled:
                        variables.field_extraction_enabled ?? false,
                    source: isLibraryPick ? "library" : "upload",
                });
            }

            // Skip on library-pick path: backend echoes library_document_id even when no upload occurred.
            if (
                !isLibraryPick &&
                typeof createdScope.library_document_id === "number"
            ) {
                trackDocumentUploaded({
                    library_document_id: createdScope.library_document_id,
                    folder_id: createdScope.library_folder_id ?? null,
                    is_knowledge_base: false,
                    source: "scope_create_upload",
                });
            }

            onClose();

            const scope_folder_id = createdScope?.library_folder_id;

            if (scope_folder_id) {
                navigate(
                    `/organizations/document-library/documents/folder/${scope_folder_id}?type_filter=scopes`
                );
            } else {
                navigate(`/organizations/scopes`);
            }
            enqueueSnackbar(
                "Your scope is being processed. You will be notified when it is complete.",
                {
                    variant: "success",
                    autoHideDuration: 1000 * 5,
                }
            );
        },
        onError: () => {
            setIsSubmitting(false);
        },
    });

    const axiosError = error as IApiAxiosError | undefined;
    const errorMessage = axiosError?.response?.data?.detail;

    const onSubmit = (data: IFormData) => {
        if (!data.document_object_name) return;
        mutate(data);
    };

    const handleDocumentChange = (value: DocumentPickerValue | null) => {
        setDocumentValue(value);
        if (value) {
            setValue("document_object_name", value.objectName);
            const title = value.title.replace(/\.pdf$/i, "");
            setValue("document_title", title);
            if (value.source === "library") {
                setValue("library_document_id", value.libraryDocumentId);
            } else {
                setValue("library_document_id", undefined);
            }
            clearErrors("document_object_name");
        } else {
            setValue("document_object_name", undefined);
            setValue("document_title", "");
            setValue("library_document_id", undefined);
        }
    };

    const validateAndSubmit = () => {
        const objectName = getValues("document_object_name");
        if (!objectName) {
            setError("document_object_name", {
                type: FileError.Required,
                message: "Please select a document to continue.",
            });
            return;
        }
        rhfHandleSubmit(onSubmit)();
    };

    return (
        <Modal
            open={open}
            onClose={() => onClose()}
            title="New Scope"
            subtitle="Upload a document and configure your analysis"
            maxWidth="sm"
            isLoading={isSubmitting}
            showCancel={!isSubmitting}
            footerText={
                errorMessage ? (
                    <Typography variant="caption" color="error">
                        {errorMessage}
                    </Typography>
                ) : undefined
            }
            actions={
                !isSubmitting
                    ? [
                          {
                              label: "Create Scope",
                              onClick: validateAndSubmit,
                              disabled: isSubmitting || !documentObjectName,
                              color: "primary",
                          },
                      ]
                    : []
            }
        >
            <FormProvider {...methods}>
                <Stack gap={3} sx={{ width: "100%" }}>
                    {/* Document Section */}
                    <FormSection>
                        <SectionLabel>Document</SectionLabel>
                        <DocumentSourceSection
                            value={documentValue}
                            onChange={handleDocumentChange}
                            errorMessage={
                                errors.document_object_name?.message as string
                            }
                            clientId={clientId}
                        />
                    </FormSection>

                    {/* Analysis Settings Section */}
                    <FormSection>
                        <SectionLabel>Analysis Settings</SectionLabel>
                        <FormCard>
                            <Stack gap={2.5}>
                                <Stack
                                    direction={isSmall ? "column" : "row"}
                                    gap={2}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <ClauseChecklistAutocomplete
                                            organizationId={organizationId}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Controller
                                            name="industry_id"
                                            control={control}
                                            render={({
                                                field: { onChange },
                                            }) => (
                                                <IndustryAutocomplete
                                                    value={industryData ?? null}
                                                    onChange={(
                                                        industry: IIndustry | null
                                                    ) =>
                                                        onChange(
                                                            industry?.id ??
                                                                undefined
                                                        )
                                                    }
                                                    helperText="Provides context for analysis"
                                                />
                                            )}
                                        />
                                    </Box>
                                </Stack>

                                <Controller
                                    name="document_title"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Document Title"
                                            placeholder="Optional - uses filename if empty"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: alpha(
                                                        theme.palette.grey[100],
                                                        0.5
                                                    ),
                                                    "&:hover": {
                                                        backgroundColor:
                                                            theme.palette
                                                                .grey[100],
                                                    },
                                                    "&.Mui-focused": {
                                                        backgroundColor:
                                                            theme.palette
                                                                .background
                                                                .paper,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                        </FormCard>
                    </FormSection>

                    {/* Admin Options */}
                    {user?.is_admin && (
                        <FormSection>
                            <SectionLabel>Advanced Options</SectionLabel>
                            <FormCard
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    backgroundColor: alpha(
                                        theme.palette.grey[100],
                                        0.3
                                    ),
                                }}
                            >
                                <Controller
                                    name="field_extraction_enabled"
                                    control={control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormControlLabel
                                            control={
                                                <StyledCheckbox
                                                    checked={value ?? false}
                                                    onChange={(e) =>
                                                        onChange(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <Stack>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        Enable field extraction
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Extract structured data
                                                        from document fields
                                                    </Typography>
                                                </Stack>
                                            }
                                            sx={{
                                                m: 0,
                                                alignItems: "flex-start",
                                            }}
                                        />
                                    )}
                                />
                            </FormCard>
                        </FormSection>
                    )}
                </Stack>
            </FormProvider>
        </Modal>
    );
};
